'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { submissionService } from '@/lib/services/submissionService';
import { createClient } from '@/lib/supabase/client';

interface LeadPopupProps {
  onClose?: () => void;
}

export default function LeadPopup({ onClose }: LeadPopupProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    course: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const courses = [
    'AI Complete Course',
    'Graphic Designing',
    'Digital Marketing',
    'Coding',
    'Video Editing',
    'Website Development',
    'App Building',
  ];

  // Helper function to set cookie
  const setCookie = (name: string, value: string, days: number) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  // Helper function to get cookie
  const getCookie = (name: string): string | null => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  useEffect(() => {
    // Check if user is a returning customer (has seen popup or submitted form)
    const hasSeenPopup = localStorage.getItem('leadPopupSeen');
    const hasSubmittedForm = localStorage.getItem('leadFormSubmitted');
    const popupSeenCookie = getCookie('leadPopupSeen');
    const formSubmittedCookie = getCookie('leadFormSubmitted');
    
    // Don't show popup if user has already seen it or submitted the form
    if (hasSeenPopup || hasSubmittedForm || popupSeenCookie || formSubmittedCookie) {
      return;
    }

    // Show popup after 1 second delay for first-time visitors
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    // Mark popup as seen in both localStorage and cookie (30 days)
    localStorage.setItem('leadPopupSeen', 'true');
    setCookie('leadPopupSeen', 'true', 30);
    if (onClose) onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Submit to Supabase
      const result = await submissionService.createLeadSubmission(formData);

      if (result) {
        // Send email notification via Edge Function
        const supabase = createClient();
        const { data: { session } } = await supabase.auth.getSession();
        
        try {
          const emailResponse = await fetch(
            `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/send-lead-notification`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
              },
              body: JSON.stringify(formData),
            }
          );

          if (!emailResponse.ok) {
            console.error('Email notification failed:', await emailResponse.text());
          }
        } catch (emailError) {
          console.error('Email notification error:', emailError);
          // Don't fail the submission if email fails
        }

        // Mark form as submitted in both localStorage and cookie (90 days)
        localStorage.setItem('leadFormSubmitted', 'true');
        setCookie('leadFormSubmitted', 'true', 90);

        setIsSuccess(true);
        setSubmitMessage('🎉 Congratulations! You\'ve unlocked 10% off on your admission. We\'ll contact you within 24 hours!');
        setFormData({ name: '', phone: '', email: '', course: '' });
        
        // Close popup after 3 seconds
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        setSubmitMessage('Something went wrong. Please try again later.');
      }
    } catch (error) {
      console.error('Lead popup submission error:', error);
      setSubmitMessage('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fadeIn"
        onClick={handleClose}
      />

      {/* Popup Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full pointer-events-auto animate-scaleIn overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 hover:bg-white transition-all shadow-lg"
            aria-label="Close popup"
          >
            <Icon name="XMarkIcon" size={20} className="text-gray-700" />
          </button>

          {/* Header with Gradient */}
          <div className="bg-gradient-to-br from-primary via-primary/90 to-secondary p-8 text-center relative overflow-hidden">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12" />
            
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-md rounded-full mb-4">
                <Icon name="SparklesIcon" size={32} className="text-white" />
              </div>
              <h2 className="font-display text-4xl uppercase tracking-tight text-white mb-2">
                Special Offer!
              </h2>
              <div className="inline-block bg-yellow-400 text-primary px-6 py-2 rounded-full font-bold text-xl mb-2">
                10% OFF
              </div>
              <p className="text-white/90 text-sm">
                On Your Instant Enquiry
              </p>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-8">
            {!isSuccess ? (
              <>
                <p className="text-center text-foreground mb-6 font-medium">
                  Enquire now and grab <span className="text-primary font-bold">10% discount</span> on admission!
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="popup_name" className="block text-sm font-semibold text-foreground mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="popup_name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                      placeholder="Enter your name"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="popup_phone" className="block text-sm font-semibold text-foreground mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="popup_phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{10}"
                      className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                      placeholder="10-digit number"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="popup_email" className="block text-sm font-semibold text-foreground mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="popup_email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                      placeholder="your@email.com"
                    />
                  </div>

                  {/* Course */}
                  <div>
                    <label htmlFor="popup_course" className="block text-sm font-semibold text-foreground mb-1">
                      Course Interested *
                    </label>
                    <select
                      id="popup_course"
                      name="course"
                      value={formData.course}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    >
                      <option value="">Select a course</option>
                      {courses.map((course) => (
                        <option key={course} value={course}>
                          {course}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-primary to-secondary text-white px-6 py-4 rounded-full text-base font-semibold uppercase tracking-wider hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Claim 10% Discount
                        <Icon name="ArrowRightIcon" size={20} />
                      </>
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <Icon name="CheckCircleIcon" size={32} className="text-green-600" />
                </div>
                <p className="text-foreground font-medium text-lg">{submitMessage}</p>
              </div>
            )}

            {/* Error Message */}
            {submitMessage && !isSuccess && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                <p className="text-red-600 text-sm font-medium">{submitMessage}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
}